<template>
  <!-- Anonymous state -->
  <div
    v-if="selectedData.isAnonymous"
    class="ctf-anonymous"
  >
    <i class="fa-light fa-user-secret" />
    <span>{{ t('dataExplorer.anonymous') }}</span>
  </div>

  <!-- Cart file -->
  <div
    v-else
    class="ctf"
  >
    <!-- ===== HERO HEADER ===== -->
    <div class="ctf-hero">
      <div class="ctf-hero__left">
        <div class="ctf-hero__avatar">
          <i class="fa-light fa-shopping-basket" />
        </div>
        <div class="ctf-hero__identity">
          <h2 class="ctf-hero__name">
            {{ title }}
          </h2>
          <div class="ctf-hero__meta">
            <span class="ctf-hero__id">#{{ selectedData.idCart }}</span>
            <span
              class="ctf-hero__badge"
              :class="isCartValid ? 'ctf-hero__badge--valid' : 'ctf-hero__badge--pending'"
            >
              <i :class="isCartValid ? 'fas fa-check-circle' : 'fas fa-clock'" />
              {{ isCartValid ? t('dataExplorer.cartFile.fields.isValid.value.yes') : t('dataExplorer.cartFile.fields.isValid.value.no') }}
            </span>
            <span
              v-if="sourceLabel"
              class="ctf-hero__source"
            >
              <i class="far fa-database" />
              {{ sourceLabel }}
            </span>
          </div>
        </div>
      </div>
      <div class="ctf-hero__dates">
        <div class="ctf-hero__date">
          <i class="fa-light fa-calendar-plus" />
          <span class="ctf-hero__date-label">{{ t('dataExplorer.cartFile.fields.creationDate') }}</span>
          <span class="ctf-hero__date-value">{{ formatDate(selectedData.date, 'YYYY-MM-DD HH:mm:ss', 'DD/MM/YYYY') }}</span>
        </div>
        <span class="ctf-hero__date-sep" />
        <div class="ctf-hero__date">
          <i class="fa-light fa-calendar-edit" />
          <span class="ctf-hero__date-label">{{ t('dataExplorer.cartFile.fields.modificationDate') }}</span>
          <span class="ctf-hero__date-value">{{ formatDate(selectedData.dateModification, 'YYYY-MM-DD HH:mm:ss', 'DD/MM/YYYY') }}</span>
        </div>
      </div>
    </div>

    <!-- ===== MAIN 2-COL LAYOUT ===== -->
    <div class="ctf-layout">
      <!-- ===== LEFT COLUMN: Cart info ===== -->
      <div class="ctf-left">
        <!-- Customer info -->
        <div class="ctf-card">
          <div class="ctf-card__header">
            <i class="fa-light fa-user" />
            <h3>{{ t('dataExplorer.orderFile.fields.customerInformations') }}</h3>
          </div>
          <div class="ctf-card__body">
            <div class="ctf-info-row">
              <div class="ctf-info-row__icon">
                <i class="fa-light fa-id-badge" />
              </div>
              <div class="ctf-info-row__content">
                <span class="ctf-info-row__label">{{ t('dataExplorer.orderFile.fields.customer') }}</span>
                <span class="ctf-info-row__value">
                  <span class="capitalize">{{ selectedData.firstname }}</span> {{ (selectedData.lastname || '').toUpperCase() }}
                  <span
                    v-if="selectedData.idCustomerShop"
                    class="ctf-info-row__sub"
                  >({{ selectedData.idCustomerShop }})</span>
                </span>
              </div>
            </div>
            <div class="ctf-info-row">
              <div class="ctf-info-row__icon">
                <i class="fa-light fa-envelope" />
              </div>
              <div class="ctf-info-row__content">
                <span class="ctf-info-row__label">{{ t('dataExplorer.orderFile.fields.email') }}</span>
                <span class="ctf-info-row__value">
                  <DataExplorerLink
                    :id="selectedData.idShopContact"
                    :value="selectedData.email"
                    :type="dataExplorerType.CONTACT_FILE"
                    :show-icon="true"
                  />
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Cart details -->
        <div class="ctf-card">
          <div class="ctf-card__header">
            <i class="fa-light fa-clipboard-list" />
            <h3>{{ t('dataExplorer.cartFile.fields.cartInfo') }}</h3>
          </div>
          <div class="ctf-card__body">
            <div class="ctf-info-row">
              <div class="ctf-info-row__icon">
                <i class="fa-light fa-circle-check" />
              </div>
              <div class="ctf-info-row__content">
                <span class="ctf-info-row__label">{{ t('dataExplorer.cartFile.fields.isValid.title') }}</span>
                <span class="ctf-info-row__value">
                  <span
                    class="ctf-validity-badge"
                    :class="isCartValid ? 'ctf-validity-badge--yes' : 'ctf-validity-badge--no'"
                  >
                    {{ isCartValid ? t('dataExplorer.cartFile.fields.isValid.value.yes') : t('dataExplorer.cartFile.fields.isValid.value.no') }}
                  </span>
                </span>
              </div>
            </div>
            <div
              v-if="isCartValid"
              class="ctf-info-row"
            >
              <div class="ctf-info-row__icon">
                <i class="fa-light fa-bag-shopping" />
              </div>
              <div class="ctf-info-row__content">
                <span class="ctf-info-row__label">{{ t('dataExplorer.cartFile.fields.orderId') }}</span>
                <span class="ctf-info-row__value">
                  <DataExplorerLink
                    :id="selectedData.isValid"
                    :value="selectedData.isValid"
                    :type="dataExplorerType.ORDER_FILE"
                    custom-key="id_order"
                    :show-icon="true"
                  />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== RIGHT COLUMN: Products & summary ===== -->
      <div class="ctf-right">
        <div class="ctf-card ctf-card--full">
          <div class="ctf-card__header ctf-card__header--no-border">
            <i class="fa-light fa-boxes-stacked" />
            <h3>{{ t('dataExplorer.cartFile.fields.cartDetails') }}</h3>
          </div>
          <div class="ctf-card__body ctf-card__body--padded">
            <List
              :list="selectedData.products.data"
              :retrieve-data="getCartProducts"
              :retrieve-data-arguments="[selectedData.idShopCart, locale]"
              :data-loaded="selectedData.products.dataLoaded"
              :no-column="true"
              key-name="idShopCartProduct"
              @loaded="handleRetrieveProducts"
            >
              <template #content="{ item }">
                <ProductItem
                  :product="item"
                />
              </template>
            </List>

            <!-- Price summary -->
            <div class="ctf-summary">
              <div class="ctf-summary__title">
                {{ t('dataExplorer.orderFile.fields.summary') }}
              </div>
              <div
                v-if="selectedData.voucherAmount"
                class="ctf-summary__row"
              >
                <span class="ctf-summary__label">{{ t('dataExplorer.orderFile.fields.voucherAmount') }}</span>
                <span class="ctf-summary__value">{{ formatNumberToCurrency(selectedData.voucherAmount || 0, selectedData.currency || 'EUR') }}</span>
              </div>
              <div class="ctf-summary__row ctf-summary__row--border">
                <span class="ctf-summary__label ctf-summary__label--bold">{{ t('dataExplorer.orderFile.fields.amountHT') }}</span>
                <span class="ctf-summary__value ctf-summary__value--bold">{{ formatNumberToCurrency(selectedData.amountHT || 0, selectedData.currency || 'EUR') }}</span>
              </div>
              <div class="ctf-summary__row">
                <span class="ctf-summary__label ctf-summary__label--bold">{{ t('dataExplorer.orderFile.fields.amountTTC') }}</span>
                <span class="ctf-summary__total">{{ formatNumberToCurrency(selectedData.amount || 0, selectedData.currency || 'EUR') }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  computed,
  ref,
  onMounted,
} from 'vue';

import DataExplorerLink from '@/components/data-explorer/fields/DataExplorerLink.vue';
import ProductItem from '@/components/data-explorer/fields/ProductItem.vue';
import List from '@/components/data-explorer/fields/List.vue';

import {
  getCartProducts,
} from '@/composables/data-explorer/DataExplorer';

import { fetchDataSources } from '@/composables/segmentations/helpers/fetchData';

import { formatDate } from '@/helpers/Date';
import { formatNumberToCurrency } from '@/helpers/Number';

import { DataExplorerDataType } from '@/types';

import { CartProduct } from '@/types/data-explorer-types';

import { useStore } from '@/store';

import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'CartFile',

  components: {
    DataExplorerLink,
    List,
    ProductItem,
  },

  setup() {
    const { t, locale } = useI18n();
    const store = useStore();

    const selectedData = computed(() => store.getters['dataExplorer/getSelectedData']);

    // Source label
    const sourceLabel = ref('');
    onMounted(async () => {
      if (!selectedData.value?.dataSourceId) return;
      const sources = await fetchDataSources();
      const found = sources.find((s: any) => s.value === Number(selectedData.value.dataSourceId));
      if (found) sourceLabel.value = found.shortLabel || found.label;
    });

    const title = computed(() => {
      if (selectedData.value) {
        return t(`dataExplorer.tabs.${selectedData.value.dataType}`, [selectedData.value.idCart]);
      }
      return '';
    });

    const isCartValid = computed(() => selectedData.value.isValid && selectedData.value.isValid !== '0');

    const dataExplorerType = ref(DataExplorerDataType);

    const handleRetrieveProducts = (cartProducts: CartProduct[]) => {
      store.dispatch(
        'dataExplorer/setAdditionnalData',
        {
          dataId: selectedData.value.dataId,
          newLists: cartProducts,
          dataKey: 'products',
        },
      );
    };

    return {
      t,
      locale,
      title,
      selectedData,
      sourceLabel,
      isCartValid,
      dataExplorerType,

      formatDate,
      formatNumberToCurrency,
      getCartProducts,
      handleRetrieveProducts,
    };
  },
});
</script>

<style lang="scss" scoped>
// ===== ANONYMOUS =====
.ctf-anonymous {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 4rem 2rem;
  color: #9ca3af;
  font-size: 1.25rem;

  i {
    font-size: 3rem;
    opacity: 0.5;
  }
}

// ===== MAIN CONTAINER =====
.ctf {
  padding: 1.25rem;
}

// ===== HERO HEADER =====
.ctf-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.25rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid #e5e7eb;

  &__left {
    display: flex;
    align-items: center;
    gap: 0.875rem;
  }

  &__avatar {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, #8b5cf6, #7c3aed);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      color: #fff;
      font-size: 1.25rem;
    }
  }

  &__identity {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  &__name {
    font-size: 1.25rem;
    font-weight: 700;
    color: #1e293b;
    margin: 0;
    line-height: 1.2;
  }

  &__meta {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  &__id {
    font-size: 0.75rem;
    color: #64748b;
    font-weight: 600;
    background: #f1f5f9;
    padding: 0.0625rem 0.4375rem;
    border-radius: 4px;
    font-family: monospace;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.6875rem;
    font-weight: 600;
    padding: 0.125rem 0.5rem;
    border-radius: 20px;

    i { font-size: 0.625rem; }

    &--valid {
      background: #dcfce7;
      color: #166534;
    }

    &--pending {
      background: #fef3c7;
      color: #92400e;
    }
  }

  &__source {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.6875rem;
    font-weight: 600;
    padding: 0.125rem 0.5rem;
    border-radius: 20px;
    background: #e0e7ff;
    color: #3730a3;

    i { font-size: 0.5rem; }
  }

  &__dates {
    display: flex;
    align-items: center;
    gap: 0.625rem;
  }

  &__date {
    display: flex;
    align-items: center;
    gap: 0.3125rem;
    font-size: 0.6875rem;
    color: #94a3b8;

    i { font-size: 0.8125rem; }
  }

  &__date-label {
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  &__date-value {
    font-weight: 700;
    color: #475569;
  }

  &__date-sep {
    width: 1px;
    height: 20px;
    background: #d1d5db;
  }
}

// ===== 2-COL LAYOUT =====
.ctf-layout {
  display: grid;
  grid-template-columns: 340px 1fr;
  gap: 1.25rem;
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
}

.ctf-left {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.ctf-right {
  min-width: 0;
}

// ===== CARD =====
.ctf-card {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;

  &--full {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  &__header {
    display: flex;
    align-items: center;
    gap: 0.4375rem;
    padding: 0.625rem 1rem;
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;

    &--no-border {
      border-bottom: none;
    }

    i {
      color: #8b5cf6;
      font-size: 0.875rem;
    }

    h3 {
      font-size: 0.75rem;
      font-weight: 700;
      color: #374151;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      margin: 0;
    }
  }

  &__body {
    padding: 0.5rem 0;

    &--padded {
      padding: 0.75rem 1rem;
    }
  }
}

// ===== INFO ROWS =====
.ctf-info-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  transition: background 0.1s;

  &:hover {
    background: #f9fafb;
  }

  &__icon {
    width: 28px;
    height: 28px;
    border-radius: 7px;
    background: #f1f5f9;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      font-size: 0.8125rem;
      color: #64748b;
    }
  }

  &__content {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.0625rem;
  }

  &__label {
    font-size: 0.6875rem;
    font-weight: 500;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  &__value {
    font-size: 0.8125rem;
    font-weight: 500;
    color: #1e293b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__sub {
    color: #94a3b8;
    font-size: 0.75rem;
    font-weight: 400;
  }
}

// ===== VALIDITY BADGE =====
.ctf-validity-badge {
  display: inline-flex;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.0625rem 0.4375rem;
  border-radius: 4px;

  &--yes {
    background: #dcfce7;
    color: #166534;
  }

  &--no {
    background: #fef3c7;
    color: #92400e;
  }
}

// ===== PRICE SUMMARY =====
.ctf-summary {
  margin-top: 1.25rem;
  border-top: 1px solid #e5e7eb;
  padding-top: 1rem;

  &__title {
    font-size: 0.8125rem;
    font-weight: 700;
    color: #374151;
    margin-bottom: 0.75rem;
  }

  &__row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.375rem 0;

    &--border {
      border-top: 1px solid #e5e7eb;
      margin-top: 0.375rem;
      padding-top: 0.75rem;
    }
  }

  &__label {
    font-size: 0.8125rem;
    color: #64748b;

    &--bold {
      font-weight: 600;
      color: #1e293b;
    }
  }

  &__value {
    font-size: 0.875rem;
    font-weight: 500;
    color: #1e293b;

    &--bold {
      font-weight: 700;
    }
  }

  &__total {
    font-size: 1.25rem;
    font-weight: 800;
    color: #8b5cf6;
  }
}
</style>
